import { ArrowLeft, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

import styles from "./ProfileAnalyticsFlow.module.css";
import { figureDataUrl, type AnalyticsOutputIntent } from "./plotting";
import { AnswerError, AnswerLoading } from "../Answer";
import { UserChatMessage } from "../UserChatMessage";
import type { ProfileAnalyticsPath } from "../../api/models";

export type AnalyticsExchange = {
    id: string;
    question: string;
    status: "loading" | "done" | "error";
    table?: Record<string, string | number>;
    path?: ProfileAnalyticsPath;
    noteCount?: number;
    outputIntent?: AnalyticsOutputIntent;
    figurePngBase64?: string;
    figureError?: string;
    error?: string;
};

interface ProfileAnalyticsFlowProps {
    exchanges: AnalyticsExchange[];
    onReturn: () => void;
    onRetry?: (question: string) => void;
}

function AnalyticsTable({ table }: { table: Record<string, string | number> }) {
    const { t } = useTranslation();
    const rows = Object.entries(table);

    if (rows.length === 0) {
        return <p className={styles.emptyTable}>{t("projectPage.analytics.emptyTable")}</p>;
    }

    return (
        <div className={styles.tableWrapper}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{t("projectPage.analytics.columnLabel")}</th>
                        <th>{t("projectPage.analytics.columnValue")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([label, value]) => (
                        <tr key={label}>
                            <td>{label}</td>
                            <td>{value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function AnalyticsFigure({ base64 }: { base64: string }) {
    const { t } = useTranslation();
    const src = figureDataUrl(base64);

    return (
        <div className={styles.figureWrapper}>
            <img className={styles.figureImage} src={src} alt={t("projectPage.analytics.figureAlt")} />
            <a className={styles.figureDownload} href={src} download="analytics-chart.png">
                <Download size={16} aria-hidden />
                {t("projectPage.analytics.downloadFigure")}
            </a>
        </div>
    );
}

export function AnalyticsFigureError({ message }: { message: string }) {
    return <p className={styles.figureError}>{message}</p>;
}

function AnalyticsResult({ exchange }: { exchange: AnalyticsExchange }) {
    const { t } = useTranslation();
    const intent = exchange.outputIntent;
    const showTable = Boolean(intent?.wantTable && exchange.table);
    const showFigure = Boolean(intent?.wantFigure && exchange.figurePngBase64);
    const showFigureError = Boolean(intent?.wantFigure && exchange.figureError && !exchange.figurePngBase64);

    return (
        <>
            {showTable && <AnalyticsTable table={exchange.table!} />}
            {showFigure && <AnalyticsFigure base64={exchange.figurePngBase64!} />}
            {showFigureError && (
                <AnalyticsFigureError message={exchange.figureError ?? t("projectPage.analytics.figureError")} />
            )}
            {exchange.noteCount !== undefined && (
                <p className={styles.noteCount}>
                    {t("projectPage.analytics.basedOnNotes", { count: exchange.noteCount })}
                </p>
            )}
        </>
    );
}

export const ProfileAnalyticsFlow = ({ exchanges, onReturn, onRetry }: ProfileAnalyticsFlowProps) => {
    const { t } = useTranslation();

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <button type="button" className={styles.returnButton} onClick={onReturn}>
                    <ArrowLeft size={18} />
                    {t("projectPage.analytics.return")}
                </button>
                {exchanges.map(exchange => (
                    <div key={exchange.id} className={styles.exchange}>
                        <UserChatMessage message={exchange.question} />
                        {exchange.status === "loading" && (
                            <div className={styles.aiMessage}>
                                <AnswerLoading />
                                <p className={styles.loadingHint}>{t("projectPage.analytics.loading")}</p>
                            </div>
                        )}
                        {exchange.status === "error" && (
                            <div className={styles.aiMessage}>
                                <AnswerError
                                    error={exchange.error ?? t("projectPage.analytics.error")}
                                    onRetry={() => onRetry?.(exchange.question)}
                                />
                            </div>
                        )}
                        {exchange.status === "done" && exchange.outputIntent && (
                            <div className={styles.aiMessage}>
                                <AnalyticsResult exchange={exchange} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
